import { ChangeDetectionStrategy, Component, inject, input, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AlertService, NotificationService } from '@app/core';
import { AssessmentApplicationMode, PostItem } from '@app/core/models';
import { ActivityService } from '@app/core/services';
import { SHARED_MODULES, SwitchComponent } from '@app/shared';
import { Checker, notNull, safeEmpty } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { Store } from '@ngrx/store';
import { Observable, of, tap } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { LoaderFn } from '../item-post/item-post.component';

@Component({
  imports: [SHARED_MODULES],
  templateUrl: './extra-exam.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExtraExamComponent {
  public readonly item = input<PostItem>();
  public readonly loaderFn = input<LoaderFn>();
  public readonly actionFn = input<any>();
  protected readonly isAllow: Signal<boolean>;
  protected readonly isStudentOrResponsible: Signal<boolean>;
  private readonly _store = inject(Store);
  private readonly _alertService = inject(AlertService);
  private readonly _activityService = inject(ActivityService);
  private readonly _notificationService = inject(NotificationService);

  constructor() {
    this.isAllow = toSignal(this._store.select(AppSelectors.isAllow([20, 36])), { initialValue: false });
    this.isStudentOrResponsible = toSignal(this.selectStudentOrResponsible(), { initialValue: false });
  }

  public onToggle(status: boolean, component: SwitchComponent) {
    const loader = this.loaderFn();
    const dimissFn = () => (component.checked = !status);
    loader(() => {
      const message = status ? 'eclass.deseja-publicar' : 'eclass.deseja-despublicar';
      return this.observable(message, status, this.item(), dimissFn);
    });
  }

  public hasAnswer(): boolean {
    return !!this.item().user_concludeds_count;
  }

  protected canSubmit(item: PostItem): boolean {
    return item.metadata.mode !== AssessmentApplicationMode.Impressa;
  }

  private observable(message: string, published: boolean, item: PostItem, dimissFn: () => void) {
    return this._alertService.confirm({ message }).pipe(
      tap((confirm) => !confirm && dimissFn()),
      notNull(),
      switchMap(() =>
        this._activityService.changeStatus(item.reference_id, { published: Number(published) }).pipe(
          catchError(() => {
            dimissFn();
            return of({ ret: 0, data: null });
          }),
          safeEmpty(),
        ),
      ),
      tap((res) => {
        if (!!res) {
          this._notificationService.success('geral.sucesso');
        } else {
          this._notificationService.error('geral.erro');
        }
      }),
    );
  }

  private selectStudentOrResponsible(): Observable<boolean> {
    return this._store.select(AppSelectors.DefaultFunction).pipe(
      notNull(),
      map((functionId) => Checker.isStudentOrResponsible(functionId)),
    );
  }
}
